<template>
  <v-container v-if="user">
    <v-app-bar app dark dense color="secondary">
      <v-toolbar-title>{{ user.firstname }} {{ user.lastname }}</v-toolbar-title>

      <v-spacer></v-spacer>

      <UserState :user="user" class="ml-4" />
    </v-app-bar>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-actions>
            <v-spacer></v-spacer>

            <v-btn v-if="pending || rejected || staffed" @click="approve(user.id)" color="green" dark>
              {{ $t("admin.user.approve") }}
            </v-btn>

            <v-btn v-if="pending || rejected || approved" @click="staff(user.id)" color="blue" dark>
              {{ $t("admin.user.staff") }}
            </v-btn>

            <v-btn v-if="pending || staffed" @click="reject(user.id)" color="red" dark>
              {{ $t("admin.user.reject") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12">
        <UserInfo :user="user" />
      </v-col>

      <template v-if="approved">
        <v-col cols="12">
          <Bookings :user="user" />
        </v-col>

        <v-col cols="12" lg="6">
          <AuditPeriodsTable :audit-periods="auditPeriods" />
        </v-col>

        <v-col cols="12" lg="6">
          <v-row>
            <v-col v-if="canCreateAuditPeriod" cols="12">
              <CreateAuditPeriodForm :user="user" />
            </v-col>

            <v-col v-if="canAlterCurrentAuditPeriod" cols="12">
              <AddFundsToAuditPeriodForm :audit-period="user.currentAuditPeriod" />
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <ReferenceableByForm :user="user" />
        </v-col>

        <v-col cols="12" v-if="$auth.may(['audit:finish'])">
          <PrecheckFirstForm :user="user" />
        </v-col>
      </template>
    </v-row>
  </v-container>
</template>

<script>
import UserInfo from "@/components/Admin/User/UserInfo";
import UserState from "@/components/Admin/User/UserState";
import AdminUserQuery from "@/gql/queries/AdminUserQuery.gql";
import RejectUserMutation from "@/gql/mutations/RejectUser.gql";
import sortAuditPeriodsDesc from "@/utils/sortAuditPeriodsDesc";
import ApproveUserMutation from "@/gql/mutations/ApproveUser.gql";
import StaffUserMutation from "@/gql/mutations/StaffUserMutation.gql";
import CreateAuditPeriodForm from "@/components/Admin/CreateAuditPeriodForm";
import PrecheckFirstForm from "@/components/Admin/PrecheckFirstForm";
import ReferenceableByForm from "@/components/Admin/ReferenceableByForm";
import mayAddFundsToAuditPeriod from "@fng/common/mayAddFundsToAuditPeriod";
import AddFundsToAuditPeriodForm from "@/components/Admin/AddFundsToAuditPeriodForm";
import AuditPeriodsTable from "@/components/Admin/User/AuditPeriodsTable.vue";
import Bookings from "@/components/Admin/User/Bookings.vue";

export default {
  components: {
    UserInfo,
    UserState,
    CreateAuditPeriodForm,
    AddFundsToAuditPeriodForm,
    AuditPeriodsTable,
    Bookings,
    ReferenceableByForm,
    PrecheckFirstForm,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      user: null,
      year: new Date().getFullYear(),
    };
  },

  computed: {
    pending() {
      return this.user.state === "PENDING";
    },

    approved() {
      return this.user.state === "APPROVED";
    },

    rejected() {
      return this.user.state === "REJECTED";
    },

    staffed() {
      return this.user.state === "STAFF";
    },

    auditPeriods() {
      return sortAuditPeriodsDesc(this.user.auditPeriods);
    },

    canCreateAuditPeriod() {
      return !this.user.currentAuditPeriod;
    },

    canAlterCurrentAuditPeriod() {
      return !!this.user.currentAuditPeriod && mayAddFundsToAuditPeriod(this.user.currentAuditPeriod.funds);
    },
  },

  apollo: {
    user: {
      query: AdminUserQuery,
      variables() {
        return {
          id: this.id,
        };
      },
    },
  },

  methods: {
    approve(id) {
      this.$apollo.mutate({
        mutation: ApproveUserMutation,
        variables: {
          id,
        },
      });
    },

    reject(id) {
      this.$apollo.mutate({
        mutation: RejectUserMutation,
        variables: {
          id,
        },
      });
    },

    staff(id) {
      this.$apollo.mutate({
        mutation: StaffUserMutation,
        variables: {
          id,
        },
      });
    },
  },
};
</script>
